/**
 * Push d'événements vers `window.dataLayer` (GTM hébergé sur la page Webflow).
 *
 * Convention : un seul event `form_submit` à la soumission réussie, avec
 * `form_type` = "devis" | "sav" pour que GTM puisse splitter les tags
 * (GA4 generate_lead, Google Ads Enhanced Conversions, Meta Lead, etc.).
 *
 * Le consentement et le hash SHA-256 des données utilisateur sont gérés
 * côté GTM (Consent Mode v2 + variable « User-Provided Data »).
 */

import type { FunnelFormData } from "@/components/Funnel/Funnel";
import type { SubmitLeadResult } from "@/lib/submitLead";

type DataLayerEvent = Record<string, unknown>;

function getDataLayer(): DataLayerEvent[] | null {
  if (typeof window === "undefined") return null;
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }
  return window.dataLayer;
}

function readUtm(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
  const out: Record<string, string> = {};
  for (const k of keys) {
    const v = p.get(k);
    if (v) out[k] = v;
  }
  return out;
}

export interface ClickIds {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
}

const CLICK_IDS_STORAGE_KEY = "komilfo_click_ids";
// Aligné sur la fenêtre d'attribution standard Google Ads (90 jours).
const CLICK_IDS_TTL_MS = 90 * 24 * 60 * 60 * 1000;

interface StoredClickIds extends ClickIds {
  capturedAt: number;
}

function readClickIdsFromUrl(): ClickIds {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const out: ClickIds = {};
  const gclid = p.get("gclid");
  const gbraid = p.get("gbraid");
  const wbraid = p.get("wbraid");
  if (gclid) out.gclid = gclid;
  if (gbraid) out.gbraid = gbraid;
  if (wbraid) out.wbraid = wbraid;
  return out;
}

function readClickIdsFromStorage(): ClickIds {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(CLICK_IDS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredClickIds;
    if (
      typeof parsed.capturedAt !== "number" ||
      Date.now() - parsed.capturedAt > CLICK_IDS_TTL_MS
    ) {
      return {};
    }
    const ids: ClickIds = {};
    if (parsed.gclid) ids.gclid = parsed.gclid;
    if (parsed.gbraid) ids.gbraid = parsed.gbraid;
    if (parsed.wbraid) ids.wbraid = parsed.wbraid;
    return ids;
  } catch {
    return {};
  }
}

/**
 * À appeler au plus tôt (chargement du bundle) pour persister les click IDs
 * arrivés dans l'URL. Permet de rattraper le cas où l'utilisateur clique une
 * ad un jour J, puis revient sur le site jusqu'à 90 jours plus tard sans le
 * param dans l'URL — on garde l'attribution pour les offline conversions.
 *
 * Stratégie : URL → écrase le storage (le click le plus récent gagne).
 */
export function captureClickIds(): void {
  if (typeof window === "undefined") return;
  const fromUrl = readClickIdsFromUrl();
  if (Object.keys(fromUrl).length === 0) return;
  try {
    const payload: StoredClickIds = { ...fromUrl, capturedAt: Date.now() };
    window.localStorage.setItem(CLICK_IDS_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage indisponible (Safari mode privé, quota) — silencieux
  }
}

/**
 * Lit les click IDs publicitaires.
 * - `gclid` : Google Ads (web)
 * - `gbraid` : Google Ads (iOS app campaigns, hors cookie)
 * - `wbraid` : Google Ads (iOS web → app, hors cookie)
 *
 * URL prioritaire (click courant), fallback localStorage (click précédent
 * dans la fenêtre d'attribution). Stockés sur le lead côté Supabase pour
 * permettre l'envoi d'offline conversions depuis le back-office.
 */
export function readClickIds(): ClickIds {
  const fromUrl = readClickIdsFromUrl();
  if (Object.keys(fromUrl).length > 0) return fromUrl;
  return readClickIdsFromStorage();
}

/**
 * Émis au tout premier engagement utilisateur (premier produit coché, clic
 * sur Suivant ou sur le lien SAV). Une seule fois par session via un guard
 * côté Funnel.
 *
 * Permet de bâtir l'audience GTM "form_started" pour le retargeting des
 * utilisateurs qui ont commencé puis abandonné en cours de funnel.
 */
export function pushFormStart(): void {
  const dl = getDataLayer();
  if (!dl) return;
  dl.push({
    event: "form_start",
    form_name: "komilfo_funnel",
    ...readUtm(),
    ...readClickIds(),
  });
}

/**
 * Émis à chaque passage d'étape (auto-advance ou bouton Suivant), juste
 * avant de quitter la step. La valeur poussée correspond à la saisie de
 * la step qu'on quitte.
 *
 * Permet des audiences segmentées :
 * - intent produit (`step_name=produits, step_value contains pergola`)
 * - geo (`step_name=localisation, code_postal commence par 75`)
 * - funnel d'abandon (dernier `step_index` atteint avant fermeture)
 */
export function pushFormStep(params: {
  stepIndex: number;
  stepName: string;
  stepValue: unknown;
  formType: "devis" | "sav" | null;
}): void {
  const dl = getDataLayer();
  if (!dl) return;
  dl.push({
    event: "form_step",
    form_name: "komilfo_funnel",
    form_type: params.formType,
    step_index: params.stepIndex,
    step_name: params.stepName,
    step_value: params.stepValue,
  });
}

export function pushFormSubmit(data: FunnelFormData, result: SubmitLeadResult): void {
  const dl = getDataLayer();
  if (!dl) return;

  const formType: "devis" | "sav" = data.demande === "SAV" ? "sav" : "devis";

  dl.push({
    event: "form_submit",
    form_name: "komilfo_funnel",
    form_type: formType,
    marketing_optin: data.marketingOptin === true,
    lead_id: result.leadId,
    magasin_id: result.magasinId,
    magasin_name: result.magasinName,
    // Google Ads Enhanced Conversions / Meta Advanced Matching
    // Données en clair : GTM se charge du hash SHA-256 côté tag.
    user_data: {
      email: data.email.trim().toLowerCase(),
      phone_number: data.telephone.replace(/\s+/g, ""),
      address: {
        first_name: data.prenom.trim(),
        last_name: data.nom.trim(),
        postal_code: data.codePostal.trim(),
        country: "FR",
      },
    },
    // Contexte additionnel utile pour la segmentation GTM / GA4
    form_data: {
      produits: formType === "devis" ? data.produits : [],
      echeance: formType === "devis" ? data.echeance || null : null,
      travaux: formType === "devis" ? data.travaux || null : null,
      habitat: formType === "devis" ? data.habitat || null : null,
      code_postal: data.codePostal.trim(),
      code_insee: data.codeInsee.trim().toUpperCase(),
      ville: data.ville.trim(),
      region: data.region || null,
      magasin: data.magasinLabel || null,
    },
    ...readUtm(),
    ...readClickIds(),
  });
}
